import {
  confirmDiscardCommentDraft,
  deferCommentCloseUntilTargetClick,
  preventNextClickAfterDiscardCancel,
  shouldLetOutsideCommentClickThrough,
} from 'helpers/commentDraft';
import {useCallback, useEffect, useRef} from 'react';

export const useCommentDisplayClose = ({
  hasUnsavedDraft,
  onClose,
  setCommentDraftDirty,
}: {
  hasUnsavedDraft: boolean;
  onClose?: () => void;
  setCommentDraftDirty: (isDirty: boolean) => void;
}) => {
  const pendingClickThroughCloseCleanupRef = useRef<(() => void) | null>(null);

  const confirmDiscardDraft = useCallback(
    (event?: MouseEvent) => {
      if (!hasUnsavedDraft) {
        return true;
      }

      const shouldDiscard = confirmDiscardCommentDraft();
      if (!shouldDiscard) {
        preventNextClickAfterDiscardCancel(event);
      }

      return shouldDiscard;
    },
    [hasUnsavedDraft]
  );

  const clearPendingClickThroughClose = useCallback(() => {
    pendingClickThroughCloseCleanupRef.current?.();
    pendingClickThroughCloseCleanupRef.current = null;
  }, []);

  const closeCommentDisplay = useCallback(() => {
    clearPendingClickThroughClose();
    setCommentDraftDirty(false);
    onClose?.();
  }, [clearPendingClickThroughClose, onClose, setCommentDraftDirty]);

  useEffect(() => {
    return () => clearPendingClickThroughClose();
  }, [clearPendingClickThroughClose]);

  const closeCommentDisplayAfterTargetClick = useCallback(() => {
    clearPendingClickThroughClose();
    pendingClickThroughCloseCleanupRef.current = deferCommentCloseUntilTargetClick(() => {
      pendingClickThroughCloseCleanupRef.current = null;
      closeCommentDisplay();
    });
  }, [clearPendingClickThroughClose, closeCommentDisplay]);

  const handleCloseComment = useCallback(
    (event?: MouseEvent) => {
      if (!confirmDiscardDraft(event)) return;

      if (shouldLetOutsideCommentClickThrough(event)) {
        closeCommentDisplayAfterTargetClick();
        return;
      }

      closeCommentDisplay();
    },
    [closeCommentDisplay, closeCommentDisplayAfterTargetClick, confirmDiscardDraft]
  );

  return {
    closeCommentDisplay,
    confirmDiscardDraft,
    handleCloseComment,
  };
};
